import { Button } from '@helvetia/components/Button'
import Input from '@helvetia/components/forms/Input'
import NumberInput from '@helvetia/components/forms/NumberInput'
import { OptionSelectorField } from '@helvetia/components/forms/OptionSelector'
import { Col, Row } from '@helvetia/components/layout'
import LinkText from '@helvetia/components/LinkText'
import { MatchMedia } from '@helvetia/components/MatchMedia'
import Spinner from '@helvetia/components/Spinner'
import Text from '@helvetia/components/Text'
import { Field, FieldArray, Form, Formik } from 'formik'
import * as React from 'react'
import { WithNamespaces, withNamespaces } from 'react-i18next'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import AgentService from '../../../core/services/agent.service'
import { TitleWrapper } from '../../_components/common.styles'
import { LifecycleView } from '../../_components/ConnectComponent'
import { AdobeEventManager } from '../../_utils/adobeAnalytics'
import { MAX_VEHICLES_INSURED } from '../../_utils/environmentConst.provider'
import { CalculatorStepper } from '../CaculatorStepper'
import { VehiclesPresenter } from './Vehicles.presenter'
import { validateFieldForm } from './Vehicles.validators'

export interface View extends LifecycleView {
  showLoading: () => void
  hideLoading: () => void
}

export interface VehiclesInfoValues {
  numberOfVehicles: number
  vehiclesToEnsure: number
  plates: string[]
}

interface Props {
  presenter: VehiclesPresenter
  onSubmit: (values: VehiclesInfoValues) => void
}

interface State {
  initialValues: VehiclesInfoValues
  isEditing: boolean
  isLoading: boolean
}

export class VehiclesSection
  extends React.Component<Props & RouteComponentProps & WithNamespaces, State>
  implements View {
  private presenter: VehiclesPresenter
  private vehicleOptions

  constructor(props) {
    super(props)

    this.presenter = props.presenter
    this.presenter.initialize(this)

    this.vehicleOptions = Array.from(
      new Array(MAX_VEHICLES_INSURED),
      (val, index) => {
        return { text: `${index + 1}`, value: index + 1 }
      },
    )

    this.state = {
      initialValues: null,
      isEditing: false,
      isLoading: false,
    }
  }

  setInitialValues = (
    initialValues: VehiclesInfoValues,
    isEditing: boolean,
  ) => {
    this.setState({
      initialValues,
      isEditing,
    })
  }

  showLoading = () => this.setState({ isLoading: true })

  hideLoading = () => this.setState({ isLoading: false })

  handleClickContinuar = (values: VehiclesInfoValues) =>
    this.presenter.onContinue(values)

  handleClickBack = () => this.presenter.onBackClick()

  handleLinkClick = (values: VehiclesInfoValues) => this.presenter.saveAndRedirect(values)


  updatePlates = (numberOfPlates: number, plates: string[]) => {
    const platesBufferArray = plates.slice(
      0,
      Math.min(numberOfPlates, plates.length, MAX_VEHICLES_INSURED),
    )
    while (
      numberOfPlates > platesBufferArray.length &&
      platesBufferArray.length < MAX_VEHICLES_INSURED
    )
      platesBufferArray.push('')
    return platesBufferArray
  }

  render() {
    const { t } = this.props
    const { isEditing } = this.state

    if (!this.state.initialValues) return <Spinner size="xl" />
    return (
      <>
        <Row>
          <Col tablet={ 12 } desktop={ 4 } verticalGutter>
            <CalculatorStepper step={ 1 } />
          </Col>

          <Col tablet={ 12 } desktop={ 8 }>
            <Row marginTop="xxs">
              <TitleWrapper mobile={ 12 } tablet={ 12 }>
                <Text as="p" color="black" size="l">
                  { t('step1.title') }
                </Text>
              </TitleWrapper>
            </Row>

            <Formik
              initialValues={ this.state.initialValues }
              onSubmit={ this.handleClickContinuar }
              validate={ values => validateFieldForm(values) }
              render={ ({
                setFieldValue,
                setFieldTouched,
                values,
                touched,
                errors,
              }) => (
                  <Form>
                    <Row marginTop="xs">
                      <Col>
                        <Field
                          component={ NumberInput }
                          name="numberOfVehicles"
                          text={ t('step1.numberOfVehicles.question') }
                          label={ t('step1.numberOfVehicles.label') }
                          required
                        />
                      </Col>
                    </Row>
                    <Row marginTop="xs" marginBottom="s">
                      <Col>
                        <MatchMedia onlyMobile>
                          <Field
                            component={ NumberInput }
                            required
                            name="vehiclesToEnsure"
                            text={ t('step1.vehiclesToEnsure.question') }
                            label={ t('step1.vehiclesToEnsure.label') }
                            onInputChange={ event => {
                              const newPlates = this.updatePlates(
                                Number(event.target.value),
                                values.plates,
                              )
                              setFieldTouched('vehiclesToEnsure')
                              setFieldValue('plates', newPlates)
                            } }
                          />
                          { touched['vehiclesToEnsure'] &&
                            errors['vehiclesToEnsure'] && (
                              <LinkText
                                onClick={ () => this.handleLinkClick(values) }
                                text={ t('step1.cancelLink') }
                                color="primary"
                              />
                            ) }
                        </MatchMedia>
                        <MatchMedia tablet>
                          <Field
                            component={ OptionSelectorField }
                            name="vehiclesToEnsure"
                            textLabel={ t('step1.vehiclesToEnsure.question') }
                            onChange={ event => {
                              const newPlates = this.updatePlates(
                                Number(event.target.value),
                                values.plates,
                              )
                              setFieldValue('plates', newPlates)
                            } }
                            optionList={ this.vehicleOptions }
                            required
                          />
                        </MatchMedia>
                      </Col>
                    </Row>
                    { values.vehiclesToEnsure > 0 &&
                      values.vehiclesToEnsure <= MAX_VEHICLES_INSURED &&
                      values.vehiclesToEnsure <= values.numberOfVehicles && (
                        <FieldArray
                          name="plates"
                          render={ arrayHelpers => (
                            <>
                              <Row verticalGutter marginTop="xxs">
                                <Text
                                  as="p"
                                  color="black"
                                  size="s"
                                  marginLeft="xxxs"
                                >
                                  { t('step1.plates.question') }
                                </Text>
                              </Row>

                              <Row>
                                { values.plates.map((plate, index) => (
                                  <Col
                                    mobile={ 12 }
                                    tablet={ 6 }
                                    desktop={ 6 }
                                    verticalGutter
                                    key={ index }
                                  >
                                    <Field
                                      key={ index }
                                      name={ `plate${index + 1}` }
                                      label={ `${t('step1.plates.label')} ${index +
                                        1}` }
                                      value={ plate }
                                      component={ Input }
                                      onInputChange={ event => {
                                        arrayHelpers.replace(
                                          index,
                                          event.target.value,
                                        )
                                        setFieldTouched(`plate${index + 1}`)
                                      } }
                                    />
                                  </Col>
                                )) }
                              </Row>
                            </>
                          ) }
                        />
                      ) }
                    <Row verticalGutter marginTop="s" marginBottom="m">
                      <Col verticalGutter mobile={ 12 } tablet={ 3 } desktop={ 3 }>
                        <Button primary type="submit">
                          { isEditing ? t('buttons.save') : t('buttons.continue') }
                        </Button>
                      </Col>
                      <Col verticalGutter mobile={ 12 } tablet={ 3 } desktop={ 3 }>
                        { isEditing ? (
                          <Button grey onClick={ this.handleClickBack }>
                            { t('buttons.cancel') }
                          </Button>
                        ) : (
                            <>
                              { AgentService.isAgentOrBroker() ? (
                                <Button
                                  grey
                                  onClick={ this.presenter.onBackToPortal }
                                >
                                  { t('buttons.backPortal') }
                                </Button>
                              ) : (
                                  <Button grey disabled>
                                    { t('buttons.back') }
                                  </Button>
                                ) }
                            </>
                          ) }
                      </Col>
                    </Row>
                  </Form>
                ) }
            />
          </Col>
        </Row>
      </>
    )
  }
}

export const VehiclesView = withNamespaces()(withRouter(VehiclesSection))
